/** Nuclius commissioning recommendation bands (engagement % 0–100). */

import { echoScoreDisplay } from '@/config/indexThresholds';
import type { WorkflowScriptListItem } from '@/types/workflow';

export const RECOMMENDATION_LABELS = [
  'Greenlight',
  'Develop',
  'Consider',
  'Hold',
  'Pass',
] as const;

export type RecommendationLabel = (typeof RECOMMENDATION_LABELS)[number];

export type RecommendationBandKey = Lowercase<RecommendationLabel>;

export type WorkflowPipelineStatus =
  | 'ACTIVE'
  | 'QUEUED'
  | 'INGESTED'
  | 'AUDIT RUN'
  | 'AUDITED';

export function recommendationFromEngagement(
  engagementPct: number | null | undefined,
): RecommendationLabel | null {
  if (engagementPct == null || Number.isNaN(engagementPct)) return null;
  if (engagementPct >= 90) return 'Greenlight';
  if (engagementPct >= 75) return 'Develop';
  if (engagementPct >= 60) return 'Consider';
  if (engagementPct >= 45) return 'Hold';
  return 'Pass';
}

export function scriptEngagementPct(script: WorkflowScriptListItem): number | null {
  if (script.engagement_percentage != null && !Number.isNaN(script.engagement_percentage)) {
    return script.engagement_percentage;
  }
  return echoScoreDisplay(script.overall_score);
}

export function scriptRecommendationLabel(
  script: WorkflowScriptListItem,
): RecommendationLabel | null {
  if (script.recommendation_label && RECOMMENDATION_LABELS.includes(script.recommendation_label as RecommendationLabel)) {
    return script.recommendation_label as RecommendationLabel;
  }
  if (script.steps.analyse !== 'complete') return null;
  return recommendationFromEngagement(scriptEngagementPct(script));
}

export function workflowPipelineStatus(script: WorkflowScriptListItem): WorkflowPipelineStatus {
  if (script.steps.analyse === 'in_progress') return 'AUDIT RUN';
  if (script.steps.analyse !== 'complete' && script.steps.upload === 'complete') return 'INGESTED';
  if (script.steps.analyse !== 'complete') return 'QUEUED';
  if (script.steps.upload === 'complete') return 'ACTIVE';
  return 'AUDITED';
}

/** Display status for registry / audit log — pipeline states or recommendation label. */
export function scriptDisplayStatus(script: WorkflowScriptListItem): string {
  if (script.steps.analyse !== 'complete') {
    return workflowPipelineStatus(script);
  }
  return scriptRecommendationLabel(script) ?? 'AUDITED';
}

export function recommendationBandKey(label: RecommendationLabel): RecommendationBandKey {
  return label.toLowerCase() as RecommendationBandKey;
}

export function recommendationTextClass(label: string): string {
  switch (label) {
    case 'Greenlight':
      return 'text-emerald-400';
    case 'Develop':
      return 'text-sky-400';
    case 'Consider':
      return 'text-amber-400';
    case 'Hold':
      return 'text-orange-400';
    case 'Pass':
      return 'text-red-400';
    default:
      return 'text-white/40';
  }
}

export function recommendationKpiTextClass(label: RecommendationLabel): string {
  return recommendationTextClass(label);
}

export type RecommendationCounts = Record<RecommendationBandKey, number> & {
  active: number;
  queued: number;
};

export function countByRecommendation(scripts: WorkflowScriptListItem[]): RecommendationCounts {
  const counts: RecommendationCounts = {
    active: 0,
    queued: 0,
    greenlight: 0,
    develop: 0,
    consider: 0,
    hold: 0,
    pass: 0,
  };

  for (const script of scripts) {
    if (script.steps.analyse !== 'complete') {
      if (script.steps.analyse === 'in_progress' || script.steps.upload === 'complete') {
        counts.active += 1;
      } else {
        counts.queued += 1;
      }
      continue;
    }
    const label = scriptRecommendationLabel(script);
    if (!label) continue;
    counts[recommendationBandKey(label)] += 1;
  }

  return counts;
}
